/**
 * Analytics Admin API Service (F100.1)
 *
 * Authenticated reads for the Site-Analytics admin tab. Mirrors the auth
 * handling style of adminApi.js (Bearer session token from localStorage).
 */

import { getAuthToken, clearAuthToken } from './adminApi';

const authedGet = async (url) => {
  const token = getAuthToken();
  if (!token) {
    return { success: false, error: 'Not authenticated', unauthorized: true };
  }

  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.status === 401 || response.status === 403) {
      clearAuthToken();
      return { success: false, error: 'Session expired. Please login again.', unauthorized: true };
    }

    if (!response.ok) {
      return { success: false, error: `Request failed (${response.status})` };
    }

    return { success: true, data: await response.json() };
  } catch (err) {
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Dashboard aggregate for the last `days` days.
 */
export const getAnalyticsSummary = (range = '7d') =>
  authedGet(`/api/admin/analytics/summary?range=${range}`);

/**
 * Full subscriber list.
 */
export const getSubscribers = () => authedGet('/api/admin/subscribers');

/**
 * Resume-request leads (form submissions with contact info).
 */
export const getResumeLeads = () => authedGet('/api/admin/resume-leads');

const authedSend = async (url, method, payload) => {
  const token = getAuthToken();
  if (!token) {
    return { success: false, error: 'Not authenticated', unauthorized: true };
  }
  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (response.status === 401 || response.status === 403) {
      clearAuthToken();
      return { success: false, error: 'Session expired. Please login again.', unauthorized: true };
    }
    if (response.status === 422) {
      return { success: false, error: 'Invalid email.' };
    }
    if (response.status === 409) {
      return { success: false, error: 'That email is already on the list.' };
    }
    if (!response.ok) {
      return { success: false, error: `Request failed (${response.status})` };
    }
    return { success: true, data: await response.json() };
  } catch (err) {
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Manually add a subscriber (admin list management).
 */
export const addSubscriber = (email) => authedSend('/api/admin/subscribers', 'POST', { email });

/**
 * Delete a subscriber (admin list management).
 */
export const deleteSubscriber = (email) => authedSend('/api/admin/subscribers', 'DELETE', { email });

/**
 * Per-post short-link aliases (rajasleem.com/li/<alias>).
 */
export const getAliases = () => authedGet('/api/admin/aliases');
export const createAlias = (path, alias) => authedSend('/api/admin/aliases', 'POST', { path, alias });
export const deleteAlias = (alias) => authedSend('/api/admin/aliases', 'DELETE', { alias });

/**
 * Campaign target for the /li /yt ... short links.
 */
export const getShortlinkTarget = () => authedGet('/api/admin/settings/shortlink-target');
export const setShortlinkTarget = (path) =>
  authedSend('/api/admin/settings/shortlink-target', 'PUT', { path });

/**
 * Wipe traffic data (views/counters/chat sessions); subscribers + leads kept.
 */
export const resetTrafficData = () => authedSend('/api/admin/analytics/reset', 'POST', {});

/**
 * Edit a subscriber's email in place (admin list management).
 */
export const editSubscriber = (email, newEmail) =>
  authedSend('/api/admin/subscribers', 'PUT', { email, new_email: newEmail });

/**
 * Download the SQLite backup - fetches with auth and triggers a browser save.
 */
export const downloadBackup = async () => {
  const token = getAuthToken();
  if (!token) {
    return { success: false, error: 'Not authenticated', unauthorized: true };
  }

  try {
    const response = await fetch('/api/admin/backup', {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.status === 401 || response.status === 403) {
      clearAuthToken();
      return { success: false, error: 'Session expired. Please login again.', unauthorized: true };
    }

    if (response.status === 404) {
      return { success: false, error: 'No data recorded yet - nothing to back up.' };
    }

    if (!response.ok) {
      return { success: false, error: `Backup failed (${response.status})` };
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : 'site-backup.db';

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (err) {
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Send a new-post newsletter announcement (F100.2). test=true sends only to
 * Raja's own email (the rehearsal path).
 */
export const sendNewsletter = ({ subject, title, description, url, test }) =>
  authedSend('/api/admin/newsletter/send', 'POST', { subject, title, description, url, test });

/**
 * Newsletter send history, newest first (F100.2).
 */
export const getNewsletterHistory = () => authedGet('/api/admin/newsletter/history');

/**
 * Published posts for the newsletter compose dropdown (F100.2).
 */
export const getNewsletterPosts = () => authedGet('/api/admin/newsletter/posts');
